/**
 * The Board's filter — Aaron, Oct 26: "filters need to be on the board
 * screen, so move it up beside the other buttons as well. But, just the
 * filter button, we don't need the associated chips with filters on that
 * screen, they get in the way." (`docs/activity.md` §8.5.)
 *
 * A square **Filter** button beside the Board's search — on a task list's
 * board before **Edit list**, on the people's before **New task** — that
 * opens **the List's own quick filter**, the component every list uses, as
 * it is: the Tasks area's sections, applied as you go, *Clear all* in its
 * footer, a sheet on a phone. **No chips**: a count over the button's corner
 * says a filter is on and how many conditions it holds, the badge the list
 * toolbar's square Filter wears.
 *
 * The Board is not built from the list engine (do-not-substitute 24): the
 * popover borrows a list of the Tasks area only for what the panel needs —
 * the field catalogue, the counts beside each choice, a relation's search
 * and *N of M match* — over the same base the List would have (the people
 * button's pick, or the list open), and the filter itself is the Board's,
 * remembered per person per device (`state.tasks.boardFilters`) and applied
 * by the server to `GET /activity/task-board` through the Tasks area's own
 * filter SQL, so the Board and the List narrow alike. Nothing this list does
 * is written back: its saved views are not even asked for, so it can never
 * land on one.
 */
import { useMemo, useRef, useState } from 'react'
import type { FilterCondition, FilterGroup, FilterNode } from '@rowted/shared'
import { QuickFilter } from '../../components/quick-filter.js'
import { useList } from '../../lib/use-list.js'
import type { RequestOptions } from '../../lib/api.js'
import { cn } from '../../lib/cn.js'
import { usePhone } from '../../lib/use-narrow.js'
import { activityRequest } from '../api.js'
import { useActivity } from '../activity-context.js'
import { Glyph } from '../glyphs.js'

const NONE: FilterGroup = { match: 'and', conditions: [] }

/**
 * The panel's own requests: Activity's (the device key), and no saved views —
 * a Board has none, and a person's default view must never open in it.
 */
function boardListRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (path === '/tasks/views' && (options.method ?? 'GET') === 'GET') return Promise.resolve({ items: [] } as T)
  return activityRequest<T>(path, options)
}

/** A filter as the server reads it: the names a pick carried are for drawing it, and stay behind. */
export function boardFilterQuery(filter: FilterGroup | null): string {
  if (!filter || filter.conditions.length === 0) return ''
  const bare = (node: FilterNode): FilterNode => {
    if ('field' in node) {
      const rest: FilterCondition = { ...(node as FilterCondition) }
      delete rest.labels
      return rest
    }
    return { ...node, conditions: node.conditions.map(bare) } as FilterNode
  }
  return JSON.stringify({ match: filter.match, conditions: filter.conditions.map(bare) })
}

export function BoardFilter({
  listId, value, onChange, height,
}: {
  /** The list open, or `null` for the people button's tasks — what *N of M* counts over. */
  listId: string | null
  value: FilterGroup | null
  onChange: (next: FilterGroup) => void
  height: 34 | 36 | 40
}) {
  const { people, unassigned } = useActivity()
  const phone = usePhone()
  const baseFilter: FilterCondition[] = useMemo(() => (listId
    ? [{ field: 'taskList', op: 'in', value: [listId] }]
    : [{ field: 'whose', op: 'in', value: [...people, ...unassigned] }]), [listId, people, unassigned])
  const list = useList('tasks', { storage: 'local', baseFilter, request: boardListRequest })
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLSpanElement>(null)
  const filter = value ?? NONE
  const count = filter.conditions.length

  return (
    <span ref={anchor} className="relative inline-flex shrink-0" data-board-filter>
      <button type="button" aria-label="Filter" title="Filter" aria-expanded={open} aria-haspopup="dialog"
        onClick={() => setOpen((was) => !was)}
        className={cn('relative inline-flex shrink-0 items-center justify-center rounded-lg border',
          open ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text-muted hover:bg-hover hover:text-text',
          /* A finger's 44 px, around a button drawn the size of the search beside it. */
          phone && 'before:absolute before:-inset-1 before:content-[""]')}
        style={{ width: height, height }}>
        <Glyph name="filter" size={15} />
        {/* Over the corner, as the list toolbar's square Filter carries it — never inside the square. */}
        {count > 0 && (
          <span data-board-filter-count
            className="tnum absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent px-[5px] text-[11px] font-bold text-white ring-2 ring-bg">
            {count}
          </span>
        )}
      </button>
      {/* The quick filter alone, with no *Advanced…*: "just the filter button" — the one button and its panel. */}
      <QuickFilter list={list} open={open} onClose={() => setOpen(false)} anchor={anchor.current}
        value={filter} onChange={onChange} />
    </span>
  )
}
